import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { subscribeGlobalLoader } from '../../utils/globalLoaderBus';

const indeterminateSlide = keyframes`
  0% { transform: translateX(-45%) scaleX(0.2); opacity: 0.55; }
  50% { transform: translateX(35%) scaleX(0.52); opacity: 1; }
  100% { transform: translateX(125%) scaleX(0.2); opacity: 0.45; }
`;

const centerPulse = keyframes`
  0%, 100% { transform: scale(0.95); opacity: 0.75; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const GlobalLoader = ({ fullscreen = false, visible, label = 'Loading' }) => {
  const theme = useTheme();
  const [eventVisible, setEventVisible] = useState(false);

  useEffect(() => {
    if (typeof visible === 'boolean') {
      return undefined;
    }

    const unsubscribe = subscribeGlobalLoader((state) => {
      setEventVisible(Boolean(state?.active));
    });

    return unsubscribe;
  }, [visible]);

  const isVisible = typeof visible === 'boolean' ? visible : eventVisible;

  const surfaceStyles = useMemo(
    () => ({
      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
      backgroundColor: alpha(theme.palette.background.paper, 0.97),
    }),
    [theme]
  );

  if (!isVisible) {
    return null;
  }

  if (fullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.modal + 30,
          backgroundColor: alpha(theme.palette.background.default, 0.82),
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 184,
            px: 2,
            py: 1.25,
            ...surfaceStyles,
          }}
        >
          <Box
            sx={{
              height: 3,
              width: '100%',
              overflow: 'hidden',
              backgroundColor: alpha(theme.palette.primary.main, 0.13),
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: '42%',
                backgroundColor: theme.palette.primary.main,
                animation: `${indeterminateSlide} 1.15s linear infinite`,
                transformOrigin: 'left center',
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: 'text.secondary',
              textAlign: 'center',
              fontWeight: 600,
              letterSpacing: '0.01em',
              animation: `${centerPulse} 1.4s ease-in-out infinite`,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.tooltip + 10,
        pointerEvents: 'none',
      }}
      aria-live="polite"
      aria-label={label}
      role="status"
    >
      <Box
        sx={{
          height: 3,
          width: '100%',
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '34%',
            backgroundColor: theme.palette.primary.main,
            animation: `${indeterminateSlide} 1.1s linear infinite`,
            transformOrigin: 'left center',
          }}
        />
      </Box>
    </Box>
  );
};

GlobalLoader.propTypes = {
  fullscreen: PropTypes.bool,
  visible: PropTypes.bool,
  label: PropTypes.string,
};

export default GlobalLoader;
